import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Download,
  Refresh,
  Description,
  PlayArrow,
  Stop,
  FirstPage,
  LastPage,
  FolderOpen,
  Close,
} from '@mui/icons-material'
import { PageShell } from '@components/common/PageShell'
import { SearchField } from '@components/common/SearchField'
import { logsApi } from '@api/endpoints/logs'
import type {
  LogFileDto,
  LogLineDto,
} from '@api/types/logs'

type ViewMode = 'paginated' | 'tail'
type FileSort = 'newest' | 'oldest' | 'name' | 'largest'

const FILE_SORT_LABELS: Record<FileSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  name: 'Name (A-Z)',
  largest: 'Largest first',
}

export default function LogsPage() {
  // The open file lives in the URL (?file=) so a link or a reload lands on the same log.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedFile = searchParams.get('file')

  // Only pop the file picker when there is nothing to show yet; a deep link goes straight to
  // the log.
  const [drawerOpen, setDrawerOpen] = useState(() => !searchParams.get('file'))

  // File list state
  const [files, setFiles] = useState<LogFileDto[]>([])
  const [filesLoading, setFilesLoading] = useState(true)
  const [filesError, setFilesError] = useState<string | null>(null)
  const [fileSort, setFileSort] = useState<FileSort>('newest')

  // Content state
  const [content, setContent] = useState<LogLineDto[]>([])
  const [contentLoading, setContentLoading] = useState(false)
  const [contentError, setContentError] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalLines, setTotalLines] = useState(0)

  // Search state
  const [searchTerm, setSearchTerm] = useState('')

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('paginated')
  const [tailLines, setTailLines] = useState(50)
  const [autoRefresh, setAutoRefresh] = useState(false)

  // Load file list
  const loadFiles = useCallback(async () => {
    setFilesLoading(true)
    setFilesError(null)
    try {
      const response = await logsApi.getLogFiles()
      // Handle both array response and wrapped response
      const fileList = Array.isArray(response) ? response : (response?.files || [])
      setFiles(fileList)
    } catch (err) {
      console.error('Failed to load log files:', err)
      setFilesError(err instanceof Error ? err.message : 'Failed to load log files')
    } finally {
      setFilesLoading(false)
    }
  }, [])

  // Load file content (paginated)
  const loadContent = useCallback(async () => {
    if (!selectedFile) return
    setContentLoading(true)
    setContentError(null)
    try {
      const response = await logsApi.getLogFileContent(selectedFile, {
        page,
        pageSize,
        searchTerm: searchTerm || undefined,
      })
      setContent(response?.lines || [])
      setTotalPages(response?.totalPages || 0)
      setTotalLines(response?.totalLines || 0)
    } catch (err) {
      console.error('Failed to load log content:', err)
      setContentError(err instanceof Error ? err.message : 'Failed to load log content')
    } finally {
      setContentLoading(false)
    }
  }, [selectedFile, page, pageSize, searchTerm])

  // Load file tail
  const loadTail = useCallback(async () => {
    if (!selectedFile) return
    setContentLoading(true)
    setContentError(null)
    try {
      const response = await logsApi.getLogFileTail(selectedFile, {
        lines: tailLines,
      })
      setContent(response?.lines || [])
      setTotalLines(response?.totalLinesReturned || 0)
      setTotalPages(1)
    } catch (err) {
      console.error('Failed to load log tail:', err)
      setContentError(err instanceof Error ? err.message : 'Failed to load log tail')
    } finally {
      setContentLoading(false)
    }
  }, [selectedFile, tailLines])

  const reload = viewMode === 'paginated' ? loadContent : loadTail

  // Initial load of files
  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  // Load content when file or params change
  useEffect(() => {
    if (selectedFile) {
      if (viewMode === 'paginated') {
        loadContent()
      } else {
        loadTail()
      }
    }
  }, [selectedFile, viewMode, loadContent, loadTail])

  // Auto-refresh for tail mode
  useEffect(() => {
    if (autoRefresh && viewMode === 'tail' && selectedFile) {
      const interval = setInterval(loadTail, 3000)
      return () => clearInterval(interval)
    }
  }, [autoRefresh, viewMode, selectedFile, loadTail])

  const sortedFiles = [...files].sort((a, b) => {
    switch (fileSort) {
      case 'name':
        return a.fileName.localeCompare(b.fileName)
      case 'largest':
        return b.sizeBytes - a.sizeBytes
      case 'oldest':
        return new Date(a.lastModifiedUtc).getTime() - new Date(b.lastModifiedUtc).getTime()
      case 'newest':
      default:
        return new Date(b.lastModifiedUtc).getTime() - new Date(a.lastModifiedUtc).getTime()
    }
  })

  const handleFileSelect = (fileName: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('file', fileName)
        return next
      },
    )
    setPage(1)
    setSearchTerm('')
    setContent([])
    setDownloadError(null)
    setDrawerOpen(false)
  }

  const handleDownload = async () => {
    if (!selectedFile) return
    setDownloadError(null)
    setDownloading(true)
    try {
      await logsApi.downloadLogFile(selectedFile)
    } catch (err) {
      console.error('Failed to download log file:', err)
      setDownloadError(err instanceof Error ? err.message : 'Failed to download log file')
    } finally {
      setDownloading(false)
    }
  }

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode) {
      setViewMode(newMode)
      setPage(1)
    }
  }

  // Get background color for log line
  const getLineBackground = (line: string): string => {
    const lowerLine = line.toLowerCase()
    if (lowerLine.includes('[err]') || lowerLine.includes('[error]') || lowerLine.includes('[fatal]') || lowerLine.includes('[ftl]')) {
      return 'rgba(244, 67, 54, 0.08)'
    }
    if (lowerLine.includes('[wrn]') || lowerLine.includes('[warn]') || lowerLine.includes('[warning]')) {
      return 'rgba(255, 152, 0, 0.08)'
    }
    return 'transparent'
  }

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString()

  const pager =
    viewMode === 'paginated' && totalPages > 1 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          columnGap: 2,
          rowGap: 1,
          py: 1.5,
        }}
      >
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={0}
          size="small"
        />
        <Typography variant="body2" color="text.secondary">
          Page {page} of {totalPages}
        </Typography>
      </Box>
    ) : null

  const fileDrawer = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      variant="temporary"
      sx={{
        // Above the AppBar (which sits at drawer+1) so the picker can take the full height
        // instead of guessing the AppBar's height with a fixed offset.
        zIndex: (t) => t.zIndex.modal,
        '& .MuiDrawer-paper': {
          width: { xs: '92vw', sm: 440 },
          maxWidth: '100vw',
          height: '100dvh',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6">Log files</Typography>
            <Typography variant="body2" color="text.secondary">
              {filesLoading ? 'Loading...' : `${files.length} file${files.length !== 1 ? 's' : ''} available`}
            </Typography>
          </Box>
          <IconButton aria-label="Refresh file list" onClick={loadFiles} disabled={filesLoading}>
            <Refresh />
          </IconButton>
          <IconButton aria-label="Close file list" onClick={() => setDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 1.5 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="log-file-sort">Sort by</InputLabel>
            <Select
              labelId="log-file-sort"
              label="Sort by"
              value={fileSort}
              onChange={(e) => setFileSort(e.target.value as FileSort)}
            >
              {(Object.keys(FILE_SORT_LABELS) as FileSort[]).map((key) => (
                <MenuItem key={key} value={key}>
                  {FILE_SORT_LABELS[key]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {filesLoading && files.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filesError ? (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={loadFiles}>
                  Retry
                </Button>
              }
            >
              {filesError}
            </Alert>
          ) : files.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No log files found</Typography>
            </Box>
          ) : (
            // A list, not a table: in a drawer a four-column table truncated the one thing that
            // matters (the filename). Here the name wraps in full and size/date sit under it.
            <List disablePadding>
              {sortedFiles.map((file) => (
                <ListItemButton
                  key={file.fileName}
                  selected={selectedFile === file.fileName}
                  onClick={() => handleFileSelect(file.fileName)}
                  divider
                  sx={{ alignItems: 'flex-start', minHeight: 56 }}
                >
                  <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                    <Description fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.fileName}
                    secondary={`${file.sizeFormatted} · modified ${formatDate(file.lastModifiedUtc)}`}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { fontFamily: 'monospace', overflowWrap: 'anywhere', fontWeight: 500 },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  )

  if (!selectedFile) {
    return (
      <PageShell title="Logs" subtitle="Server log files">
        {fileDrawer}
        <Box sx={{ textAlign: 'center', py: { xs: 4, md: 8 } }}>
          <Description sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select a log file to view
          </Typography>
          <Button
            variant="contained"
            startIcon={<FolderOpen />}
            onClick={() => setDrawerOpen(true)}
            sx={{ mt: 2, minHeight: 44 }}
          >
            Browse log files
          </Button>
        </Box>
      </PageShell>
    )
  }

  return (
    <PageShell
      title="Logs"
      subtitle={
        <Box component="span" sx={{ display: 'block' }}>
          <Box component="span" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>
            {selectedFile}
          </Box>
          {' · '}
          {viewMode === 'tail'
            ? `${totalLines.toLocaleString()} lines shown`
            : `${totalLines.toLocaleString()} ${searchTerm ? 'matching' : 'total'} lines`}
        </Box>
      }
      actions={
        <>
          <Button variant="outlined" startIcon={<FolderOpen />} onClick={() => setDrawerOpen(true)}>
            Files
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={reload} disabled={contentLoading}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} /> : <Download />}
            onClick={handleDownload}
            disabled={downloading}
          >
            Download
          </Button>
        </>
      }
    >
      {fileDrawer}

      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {/* Controls: wrap onto as many lines as the screen needs */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
          <ToggleButton value="paginated" sx={{ gap: 0.5, minHeight: 40 }}>
            <FirstPage fontSize="small" />
            Pages
          </ToggleButton>
          <ToggleButton value="tail" sx={{ gap: 0.5, minHeight: 40 }}>
            <LastPage fontSize="small" />
            Tail
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'paginated' ? (
          <>
            <SearchField
              value={searchTerm}
              onSearch={(q) => {
                setSearchTerm(q)
                setPage(1)
              }}
              placeholder="Search in log"
              maxWidth={320}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 240px' } }}
            />
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="log-page-size">Per page</InputLabel>
              <Select
                labelId="log-page-size"
                value={pageSize}
                label="Per page"
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
          </>
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="log-tail-lines">Lines to show</InputLabel>
              <Select
                labelId="log-tail-lines"
                value={tailLines}
                label="Lines to show"
                onChange={(e) => setTailLines(Number(e.target.value))}
              >
                <MenuItem value={25}>25 lines</MenuItem>
                <MenuItem value={50}>50 lines</MenuItem>
                <MenuItem value={100}>100 lines</MenuItem>
                <MenuItem value={200}>200 lines</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              startIcon={autoRefresh ? <Stop /> : <PlayArrow />}
              onClick={() => setAutoRefresh(!autoRefresh)}
              color={autoRefresh ? 'primary' : 'inherit'}
              sx={{ minHeight: 40 }}
            >
              {autoRefresh ? 'Stop auto-refresh' : 'Auto-refresh'}
            </Button>
            {autoRefresh && <Chip label="Refreshing every 3s" color="primary" size="small" variant="outlined" />}
          </>
        )}
      </Box>

      {/* What the two modes mean, as text: it used to live only in hover tooltips */}
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.5 }}>
        {viewMode === 'paginated'
          ? 'Pages: the whole file from the start, oldest lines first, with search.'
          : `Tail: the last ${tailLines} lines of the file, newest at the bottom.`}
      </Typography>

      {pager}

      {/* The log flows with the page (no fixed-height inner scroller): the main region already
          scrolls, and a second scroll box inside it is fiddly on a phone. Lines wrap, so it
          never needs to scroll sideways. */}
      <Box sx={{ bgcolor: 'grey.900', borderRadius: 1, overflow: 'hidden' }}>
        {contentLoading && content.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : contentError ? (
          <Alert
            severity="error"
            sx={{ m: 2 }}
            action={
              <Button color="inherit" size="small" onClick={reload}>
                Retry
              </Button>
            }
          >
            {contentError}
          </Alert>
        ) : content.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'grey.400' }}>
              {searchTerm ? 'No matching lines found' : 'No log entries'}
            </Typography>
          </Box>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 0,
              fontFamily: 'monospace',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              lineHeight: 1.5,
            }}
          >
            {content.map((line, index) => (
              <Box
                key={line.lineNumber || index}
                sx={{
                  display: 'flex',
                  bgcolor: getLineBackground(line.content),
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: 'grey.600',
                    minWidth: { xs: 44, sm: 60 },
                    textAlign: 'right',
                    pr: { xs: 1, sm: 2 },
                    pl: { xs: 0.5, sm: 1 },
                    userSelect: 'none',
                    borderRight: 1,
                    borderColor: 'grey.800',
                    flexShrink: 0,
                  }}
                >
                  {line.lineNumber}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: 'grey.100',
                    pl: { xs: 1, sm: 2 },
                    pr: 1,
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {line.content}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {pager}
    </PageShell>
  )
}
